/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { ICoreContext, IImageSettings, ITelemetry } from '@msdyn365-commerce/core';
import { ICartState } from '@msdyn365-commerce/global-state';
import { AsyncResult, ProductAvailableQuantity, ProductPrice, ProductSearchResult } from '@msdyn365-commerce/retail-proxy';
import * as React from 'react';
import ProductCard, { ISmweSearchResultContainerParentProps } from '../../../components/product.component';
import { ISmweSearchResultContainerResources } from '../smwe-search-result-container.props.autogenerated';

interface IProductSearchResultItems {
    products: ProductSearchResult[];
    context: ICoreContext;
    resources: ISmweSearchResultContainerResources;
    telemetry: ITelemetry;
    imageSettings?: IImageSettings;
    moduleType: string;
    moduleId: string;
    parentProps: ISmweSearchResultContainerParentProps;
    addToCart?: boolean;
    cart?: AsyncResult<ICartState>;
    productAvailability?: ProductAvailableQuantity[] | undefined;
    clubPricing?: ProductPrice[] | undefined;
    showQuantityAsDropdown?: boolean;
}

export const ProductSearchResultItems: React.FC<IProductSearchResultItems> = ({
    products,
    context,
    imageSettings,
    resources,
    moduleType,
    moduleId,
    parentProps,
    addToCart,
    cart,
    productAvailability,
    clubPricing,
    showQuantityAsDropdown
}) => {
    return (
        <ul className='list-unstyled' role='presentation'>
            {products.map((product: ProductSearchResult, index: number) => {
                const availibity = productAvailability?.find((item) => {
                    return item.ProductId === product.RecordId;
                });
                const clubPrice = clubPricing?.find((item) => {
                    return item.ProductId === product.RecordId;
                });
                return (
                    <li className='ms-product-search-result__item' key={index}>
                        <ProductCard
                            context={context}
                            imageSettings={imageSettings}
                            freePriceText={resources.priceFree}
                            originalPriceText={resources.originalPriceText}
                            currentPriceText={resources.currentPriceText}
                            ratingAriaLabel={resources.ratingAriaLabel}
                            id={moduleId}
                            typeName={moduleType}
                            data={{ product: product }}
                            parentProps={parentProps}
                            addToCart={addToCart}
                            addToCartText={resources.addToCartTextPlp}
                            addToCartMessage={resources.addToCartMessagePlp}
                            giftCardText={resources.giftCardText}
                            displayQuantitySlider={addToCart}
                            cart={cart}
                            availability={availibity}
                            clubPrice={clubPrice}
                            showQuantityAsDropdown={showQuantityAsDropdown || false}
                        />
                    </li>
                );
            })}
        </ul>
    );
};